import db from "../database";

/**
 * BUG-2636: which table owns a KOT line. HMRES01 15-Oct 11:27:09: waiterhm2 had F5 open, then
 * opened H2; the fire wrote F5's 8 lines into H2's session 57322004 (cartDetails Rs 4,312.47
 * = H2's 10 + F5's 8) and HMRS/11181 was later saved with tableId F20. cart.tableId is re-stamped
 * from the table that was opened (handleTable), so the tableId alone cannot catch it: the line ids
 * are checked against the server `lines` of the OTHER tables' open sessions. Only `lines` count —
 * a contaminated cart / wsData snapshot must never make the real owner's lines look foreign.
 * Transfer keeps the fbOrderId and merge mints new line ids (useTableFeatureHandlers), so both stay clean.
 */
export const findForeignCartLines = ({ items = [], tableId, fbOrderId, sessions = [] } = {}) => {
  if (!tableId || !Array.isArray(items) || items.length === 0) return [];
  const owner = new Map();
  for (const s of sessions) {
    const sTable = s?.cwrFbTableId || s?.fbTableId;
    if (!s || s.fbOrderStatus !== "IP" || !sTable || String(sTable) === String(tableId)) continue;
    if (fbOrderId && s.fbOrderId === fbOrderId) continue;
    for (const line of s.lines || []) {
      if (line?.fbOrderLineId && !owner.has(line.fbOrderLineId)) owner.set(line.fbOrderLineId, { tableId: sTable, fbOrderId: s.fbOrderId });
    }
  }
  if (owner.size === 0) return [];
  const foreign = [];
  for (const item of items) {
    const o = item?.fbOrderLineId && owner.get(item.fbOrderLineId);
    if (o) foreign.push({ item, ...o });
  }
  return foreign;
};

/** Every local table session, or null when db.fbOrderData cannot be read (the check is then skipped). */
export const loadTableSessions = async () => {
  try {
    return await db.fbOrderData.toArray();
  } catch {
    return null;
  }
};

const tableLabel = (row, id) => row?.name || row?.title || row?.tableName || String(id);

/**
 * BUG-2636: Prepare Bill only for a cart that is this store's table and holds only its own lines.
 * HMRS/11181 was saved with tableId F20 (a table of another store) although its lines were H2's.
 * Returns null when the bill may be prepared (also when a read fails: the check is skipped),
 * otherwise { reason, message, ... }. Rows still IP on the cart's own table are never "other"
 * (a transfer leaves the moved session on its new table with the same fbOrderId).
 */
export const checkPrepareBillTable = async (cart) => {
  if (!cart?.tableId) return null;
  let tableRow;
  let sessions;
  try {
    tableRow = await db.tableData.get(cart.tableId);
    sessions = await db.fbOrderData.toArray();
  } catch {
    return null;
  }
  if (!tableRow) {
    return { reason: "unknown-table", tableId: cart.tableId, message: "This bill is not for a table of this store. Open the table again from the table screen, then prepare the bill." };
  }
  const ownIds = new Set(sessions.filter((s) => String(s?.cwrFbTableId || s?.fbTableId) === String(cart.tableId)).map((s) => s.fbOrderId));
  const foreign = findForeignCartLines({ items: cart.items, tableId: cart.tableId, sessions: sessions.filter((s) => !ownIds.has(s.fbOrderId)) });
  if (foreign.length === 0) return null;
  let otherRow = null;
  try {
    otherRow = await db.tableData.get(foreign[0].tableId);
  } catch {
    /* name only */
  }
  const other = tableLabel(otherRow, foreign[0].tableId);
  return {
    reason: "foreign-lines",
    tableId: cart.tableId,
    foreign,
    message: `${foreign.length} item(s) in this cart belong to table ${other}. Open table ${tableLabel(tableRow, cart.tableId)} again from the table screen, then prepare the bill.`,
  };
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** The cart without the given lines; total / tax lose the removed lines' amounts. */
export const withoutCartLines = (cart, lineIds) => {
  if (!cart || !lineIds?.size) return cart;
  const removed = (cart.items || []).filter((i) => lineIds.has(i?.fbOrderLineId));
  if (removed.length === 0) return cart;
  const net = removed.reduce((s, i) => s + num(i.nettotal), 0);
  const tax = removed.reduce((s, i) => s + num(i.taxAmount), 0);
  const round = (v) => Math.round(v * 100) / 100;
  return {
    ...cart,
    items: cart.items.filter((i) => !lineIds.has(i?.fbOrderLineId)),
    total: cart.total !== undefined ? round(num(cart.total) - net) : cart.total,
    tax: cart.tax !== undefined ? round(num(cart.tax) - tax) : cart.tax,
  };
};
